import React, { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import Form from '../../../../../components/form/Form';
import Panel from '../../../../../components/Panel';
import Select from '../../../../../components/input/Select';
import {
  BANNER_WIDGET_LAYOUT_TYPE_LABEL_V2,
  BANNER_WIDGET_LINK_TYPE_SELECT_LIST,
  WIDGET_TYPE_LABEL_V2,
  WIDGET_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/widgetConstantsV2';
import {
  WIDGET_TYPE_V2,
  BannerWidgetDetailV2,
  BANNER_WIDGET_LAYOUT_TYPE_V2,
  BannerWidgetRequestPayloadV2,
  BANNER_WIDGET_LINK_TYPE_V2,
  WidgetItemBannerItem,
  LinkItem,
} from '../../../../../types/widget';
import WidgetLayoutSelect from '../../common/components/WidgetLayoutSelect';
import Notice from '../../../../../components/Notice';
import Input from '../../../../../components/input/Input/Input';
import { InfiniteRangePicker } from '../../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../../types/common/date';
import FormListTab, { TabItem } from '../../../../../components/input/FormListTab/FormListTab';
import { BRAND, EVENT, PRODUCT, REVIEW, TAG } from '../../../../../constants/queryKeys';
import { eventListFetcher } from '../../common/fetchers/eventListFetcher';
import SearchListTrigger from '../../../../../components/SearchListTrigger';
import TextArea from '../../../../../components/input/TextArea/TextArea';
import FormWithTab from '../../../../../components/form/Form/FormWithTab';
import ImageAndVideoUploadV2 from '../../../../../components/input/ImageAndVideoUploadV2';
import { useBannerWidgetFormRules } from './useBannerWidgetFormRules';
import { brandListFetcher } from '../../common/fetchers/brandListFetcher';
import {
  brandReactTableColumns,
  productReactTableColumns,
  tagReactTableColumns,
  reviewReactTableColumns,
  eventReactTableColumns,
} from '../../common/columns';
import { productListFetcher } from '../../common/fetchers/productListFetcher';
import { reviewListFetcher } from '../../common/fetchers/reviewListFetcher';
import { tagListFetcher } from '../../common/fetchers/tagListFetcher';
import { BrandItemV2 } from '../../../../../types/brand/list';
import { EventItemV2 } from '../../../../../types/event';
import { ProductItemV2 } from '../../../../../types/product';
import { ReviewItemV2 } from '../../../../../types/review/list';
import { TagItemV2 } from '../../../../../types/tag';
import UrlInput from './UrlInput';
import LinkId from '../../../../../components/input/LinkId';
import WidgetFormFooter from '../../WidgetFormFooter';
import BannerWidgetPreviewModal from './BannerWidgetPreviewModal';
import useBannerWidgetForm from './useBannerWidgetForm';
import Warning from '../../../../../components/Warning';
import Switch from '../../../../../components/input/Switch';
import { FieldError } from '../../../../../types/common/form';
import { textNormalize } from '../../../../../lib/utils/form/normalize';

const BANNER_WIDGET_ITEM_MAX_COUNT = {
  [BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_MARGIN]: 10,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_NO_MARGIN]: 10,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_1_DOT_5]: 10,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_2_DOT_5]: 10,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.GRID_1X2]: 2,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.GRID_2X2]: 4,
  [BANNER_WIDGET_LAYOUT_TYPE_V2.SCROLL_NX1]: 10,
} as const;

const getItemCountWarningMessage = (type?: BANNER_WIDGET_LAYOUT_TYPE_V2): string => {
  switch (type) {
    case BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_MARGIN:
    case BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_NO_MARGIN:
      return '위젯 아이템을 최소 1개 선택해주세요.';
    case BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_1_DOT_5:
    case BANNER_WIDGET_LAYOUT_TYPE_V2.GRID_1X2:
    case BANNER_WIDGET_LAYOUT_TYPE_V2.SCROLL_NX1:
      return '위젯 아이템을 최소 2개 선택해주세요.';
    case BANNER_WIDGET_LAYOUT_TYPE_V2.SWIPE_2_DOT_5:
      return '위젯 아이템을 최소 3개 선택해주세요.';
    case BANNER_WIDGET_LAYOUT_TYPE_V2.GRID_2X2:
      return '위젯 아이템을 2개 단위로 선택해주세요.';
    default:
      return '위젯 아이템을 최소 1개 선택해주세요.';
  }
};

interface BannerWidgetFormProps {
  isEdit: boolean;
  form: WrappedFormUtils;
  initialValue: Partial<BannerWidgetDetailV2>;
  onChangeType: (type: WIDGET_TYPE_V2) => void;
  onFinish: (value: BannerWidgetRequestPayloadV2) => void;
}

const BannerWidgetForm = (props: BannerWidgetFormProps): JSX.Element => {
  const { isEdit, initialValue, form, onFinish, onChangeType } = props;
  const [currentTab, setCurrentTab] = useState<number>();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const { layoutType, widgetItemBanners, handleChangeLayoutType } = useBannerWidgetForm({
    form,
    initialValue,
  });

  useEffect(() => {
    if (!currentTab && initialValue.widgetItemBanners && initialValue.widgetItemBanners.length)
      setCurrentTab(initialValue.widgetItemBanners[0].id);
  }, [initialValue]);

  const formRule = useBannerWidgetFormRules(form);

  const handleFinish = (value: BannerWidgetDetailV2): void => onFinish(new BannerWidgetRequestPayloadV2(value));

  const handleFinishFailed = (errors: FieldError[]): void => {
    const tabError = errors.find((err) => err.field === 'widgetItemBanners');
    if (tabError && tabError.errors && tabError.errors.length > 0) {
      const firstErrorTab = tabError.errors[0].id;
      if (firstErrorTab) setCurrentTab(firstErrorTab);
    }
  };

  const checkValidSelect = (value: BANNER_WIDGET_LAYOUT_TYPE_V2): string | null => {
    if (widgetItemBanners && widgetItemBanners?.length > BANNER_WIDGET_ITEM_MAX_COUNT[value]) {
      return `현재 선택된 위젯 배너 아이템 개수는 ${widgetItemBanners?.length}개 입니다. 변경하실 위젯 배너(${BANNER_WIDGET_LAYOUT_TYPE_LABEL_V2[value]}) 아이템 개수는 최대 ${BANNER_WIDGET_ITEM_MAX_COUNT[value]}개 입니다. 배너 아이탬 개수를 ${BANNER_WIDGET_ITEM_MAX_COUNT[value]}개 이하로 설정하시고 변경하십시오.`;
    }

    return null;
  };

  const bannerLinkId = (item: WidgetItemBannerItem): JSX.Element => {
    const trigger = (openModal: () => void): JSX.Element => (
      <SearchListTrigger
        value={item.link}
        triggerText="ID 선택"
        linkText={`${item.link?.name}(${item.link?.id})`}
        onClick={openModal}
      />
    );

    switch (item.linkType) {
      case BANNER_WIDGET_LINK_TYPE_V2.BRAND:
        return (
          <LinkId
            queryKey={BRAND.LIST}
            fetcher={brandListFetcher}
            columns={brandReactTableColumns}
            formatter={(brand: BrandItemV2): LinkItem => ({ id: brand.id, name: brand.name })}
            trigger={trigger}
          />
        );
      case BANNER_WIDGET_LINK_TYPE_V2.EVENT:
        return (
          <LinkId
            queryKey={EVENT.LIST}
            fetcher={eventListFetcher}
            columns={eventReactTableColumns}
            formatter={(event: EventItemV2): LinkItem => ({ id: event.id, name: event.mainTitle })}
            trigger={trigger}
          />
        );
      case BANNER_WIDGET_LINK_TYPE_V2.PRODUCT:
        return (
          <LinkId
            queryKey={PRODUCT.LIST}
            fetcher={productListFetcher}
            columns={productReactTableColumns}
            formatter={(product: ProductItemV2): LinkItem => ({ id: product.id, name: product.name })}
            trigger={trigger}
          />
        );
      case BANNER_WIDGET_LINK_TYPE_V2.REVIEW:
        return (
          <LinkId
            queryKey={REVIEW.LIST}
            fetcher={reviewListFetcher}
            columns={reviewReactTableColumns}
            formatter={(review: ReviewItemV2): LinkItem => ({ id: review.id, name: review.productName })}
            trigger={trigger}
          />
        );
      case BANNER_WIDGET_LINK_TYPE_V2.TAG_DETAIL_GALLERY:
      case BANNER_WIDGET_LINK_TYPE_V2.TAG_DETAIL_FEED:
        return (
          <LinkId
            queryKey={TAG.LIST}
            fetcher={tagListFetcher}
            columns={tagReactTableColumns}
            formatter={(tag: TagItemV2): LinkItem => ({ id: tag.id, name: tag.name })}
            trigger={trigger}
          />
        );
      default:
        return (
          <LinkId
            queryKey={BRAND.LIST}
            fetcher={brandListFetcher}
            columns={brandReactTableColumns}
            formatter={(brand: BrandItemV2): LinkItem => ({ id: brand.id, name: brand.name })}
            trigger={trigger}
          />
        );
    }
  };

  return (
    <FormWithTab
      name="widgetItemBanners"
      form={form}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      initialValue={initialValue}
    >
      <Panel title="위젯 정보">
        <Panel.Section>
          <Form.Item
            name="type"
            label="위젯 타입"
            notice={isEdit ? <Notice>* 위젯 복사, 편집 시에는 위젯 데이터 타입을 변경할 수 없습니다.</Notice> : null}
          >
            {isEdit ? (
              <span>{WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.BANNER]}</span>
            ) : (
              <Select list={WIDGET_TYPE_SELECT_LIST_V2} onChange={onChangeType} style={{ width: 140 }} />
            )}
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="layoutType" label="위젯 레이아웃 타입">
            <WidgetLayoutSelect
              type={WIDGET_TYPE_V2.BANNER}
              checkValidSelect={(value): string | null => checkValidSelect(value as BANNER_WIDGET_LAYOUT_TYPE_V2)}
              onChange={(layoutType): void => handleChangeLayoutType(layoutType as BANNER_WIDGET_LAYOUT_TYPE_V2)}
            />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="mainTitle"
            label="위젯 타이틀"
            rules={formRule.mainTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="subTitle"
            label="위젯 서브 타이틀"
            rules={formRule.subTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 서브 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="memo" label="메모" maxCount={1000} rules={formRule.memo.rules} normalize={textNormalize}>
            <TextArea />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="exposurePeriod"
            label="위젯 노출기간"
            notice={<Notice>* 해당 위젯이 메인/서브 페이지에서 노출되는 기간입니다.</Notice>}
          >
            <InfiniteRangePicker format={DATE_FORMAT.FULL_DASH_FORMAT} showTime={true} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="노출되고 있는 페이지">
            <span>{initialValue?.currentExposurePage || '-'}</span>
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="홈에서 배너 위젯 노출 정보를 결정하는 옵션">
        <Panel.Section>
          <Form.Item name="widgetItemBanners" rules={formRule.widgetItemBanners.rules}>
            <FormListTab
              max={layoutType ? BANNER_WIDGET_ITEM_MAX_COUNT[layoutType] : 0}
              notice={
                <>
                  <Notice>* 드래그 앤 드랍으로 위젯 섹션의 순서를 변경할 수 있습니다.</Notice>
                  <Warning>{getItemCountWarningMessage(layoutType)}</Warning>
                </>
              }
              initialTabItemValue={{
                mobileImage: [],
                pcImage: [],
                memo: '',
                linkType: BANNER_WIDGET_LINK_TYPE_V2.BRAND,
              }}
              onChangeActiveKey={setCurrentTab}
              activeKey={currentTab}
              items={(value: BannerWidgetDetailV2['widgetItemBanners']): TabItem[] =>
                value.map((item, idx) => ({
                  id: item.id,
                  title: `배너 - ${idx + 1}`,
                  content: (tabForm) => (
                    <>
                      <Panel.Section>
                        <Form.Item
                          name="mobileImage"
                          label="App 이미지"
                          required={true}
                          rules={formRule.widgetItemBanners.innerRules(item).mobileImage.rules}
                          notice={
                            <Notice>
                              <p>* 각 배너 별 App 홈에 노출 될 이미지입니다.</p>
                            </Notice>
                          }
                        >
                          <ImageAndVideoUploadV2 modalTitle="APP 이미지" />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="pcImage"
                          label="PC 이미지"
                          required={true}
                          rules={formRule.widgetItemBanners.innerRules(item).pcImage.rules}
                          notice={
                            <Notice>
                              <p>* 각 배너 별 PC 홈에 노출 될 이미지입니다.</p>
                            </Notice>
                          }
                        >
                          <ImageAndVideoUploadV2 modalTitle="PC 이미지" />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="memo"
                          label="메모"
                          maxCount={250}
                          normalize={textNormalize}
                          rules={formRule.widgetItemBanners.innerRules(item).memo.rules}
                        >
                          <TextArea />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item name="linkType" label="링크 종류" initialValue={BANNER_WIDGET_LINK_TYPE_V2.BRAND}>
                          <Select
                            list={BANNER_WIDGET_LINK_TYPE_SELECT_LIST}
                            style={{ width: 250 }}
                            onChange={(): void => {
                              //링크 종류 변경시 link 리셋
                              tabForm.setFieldsValue({ link: null });
                            }}
                          />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.widgetItemBanners.innerRules(item).link.hideIf}>
                        <Form.Item
                          name="link"
                          label="링크 ID"
                          required={true}
                          rules={formRule.widgetItemBanners.innerRules(item).link.rules}
                          notice={
                            <Notice>
                              * 링크 종류가 '브랜드 상세 페이지', '기획전 상세 페이지', '상품 상세 페이지', '후기 상세',
                              '태그 상세 - 갤러리', '태그 상세 - 피드' 인 경우에는 링크 ID 를 반드시 설정하셔야 합니다.
                            </Notice>
                          }
                        >
                          {bannerLinkId(item)}
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.widgetItemBanners.innerRules(item).linkUrl.hideIf}>
                        <Form.Item
                          name="linkUrl"
                          label="웹 페이지"
                          rules={formRule.widgetItemBanners.innerRules(item).linkUrl.rules}
                          normalize={textNormalize}
                          notice={<Notice>* 링크 종류가 ‘웹페이지' 인 경우에는 URL을 반드시 입력하셔야 합니다.</Notice>}
                        >
                          <UrlInput
                            canCategorySearch={
                              tabForm.getFieldValue('linkType') === BANNER_WIDGET_LINK_TYPE_V2.CATEGORY
                            }
                          />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.widgetItemBanners.innerRules(item).isUseBannerTitle.hideIf}>
                        <Form.Item
                          name="isUseBannerTitle"
                          label="배너 타이틀 여부"
                          valuePropName="checked"
                          notice={
                            <Notice>
                              *위젯 레이아웃이 스크롤 N X 1일 경우에만 타이틀을 ON/OFF 할 수 있습니다.
                              <br />
                              선택된 배너에 대한 타이틀과 서브 타이틀을 작성하는 입력칸입니다.
                            </Notice>
                          }
                        >
                          <Switch
                            onChange={(checked): void => {
                              if (!checked) {
                                tabForm.setFieldsValue({ title: '', contents: '' });
                              }
                            }}
                          />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.widgetItemBanners.innerRules(item).title.hideIf}>
                        <Form.Item
                          name="title"
                          label="배너 타이틀"
                          rules={formRule.widgetItemBanners.innerRules(item).title.rules}
                          normalize={textNormalize}
                          notice={<Notice>* 배너 타이틀의 글자 수는 최대 20자입니다.</Notice>}
                        >
                          <Input />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.widgetItemBanners.innerRules(item).contents.hideIf}>
                        <Form.Item
                          name="contents"
                          label="배너 서브 타이틀"
                          rules={formRule.widgetItemBanners.innerRules(item).contents.rules}
                          normalize={textNormalize}
                          notice={<Notice>* 배너 서브 타이틀의 글자 수는 최대 50자입니다.</Notice>}
                        >
                          <Input />
                        </Form.Item>
                      </Panel.Section>
                    </>
                  ),
                }))
              }
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Form.Item>
        <WidgetFormFooter
          typeLabel={WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.BANNER]}
          onClickPreview={(): void => setIsPreviewModalOpen(true)}
        />
      </Form.Item>
      <BannerWidgetPreviewModal
        isOpen={isPreviewModalOpen}
        detail={form.getFieldsValue() as BannerWidgetDetailV2}
        onClose={(): void => setIsPreviewModalOpen(false)}
      />
    </FormWithTab>
  );
};

export default Form.create<BannerWidgetFormProps>()(BannerWidgetForm);
